import React from "react";
import { Tag } from "antd";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
} from "../../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../../components/upload/shared/uploadReport";
import {
  DaySheetScope,
  normaliseDaySheet,
  normaliseEquipmentSheet,
  siteDailyApis,
  VARIABLE_HEAD_KEYS,
} from "../../../../api/SiteDaily";
import {
  bucketRows,
  DownloadBlocked,
  FilledSheetDownload,
  inBatches,
  messageColumns,
  moneyColumn,
  numberColumn,
  reportMessageColumns,
  reportStatus,
  rowColumn,
  sheetDate,
  statusColumn,
} from "./dailyUploadShared";
import { writeValidatedWorkbook } from "./filledWorkbook";

// Mechanical upload — one row per machine per task-day, with a quantity and
// rate for each operational cost head (units fixed per head, so they sit in the
// headers). Owning cost is never in the file: the preview prices it from the
// rate card × hours. Each task-day is the sheet's replace-set.

const HEAD_LABEL: Record<string, string> = {
  repair: "Repair",
  misc: "Misc",
  electricity: "Electricity",
  fuel: "Fuel",
  lubricants: "Lubricants",
};
// Every consumed head is hours × ₹/hr, the unit the rate card prices it in —
// so the report compares target and actual rates like for like. (Nos / Unit /
// Litre until 2026-10-05; the server refuses a sheet still carrying those.)
const HEAD_UNIT: Record<string, string> = {
  repair: "Hr",
  misc: "Hr",
  electricity: "Hr",
  fuel: "Hr",
  lubricants: "Hr",
};
const HEAD_COLUMNS = VARIABLE_HEAD_KEYS.flatMap((head) => [
  `${HEAD_LABEL[head]} Qty (${HEAD_UNIT[head]})`,
  `${HEAD_LABEL[head]} Rate`,
]);

// There is no blank template, deliberately — the same reasoning as
// Material-Stores: a hand-typed sheet means hand-typed task ids and asset
// codes, which is what the filled day sheet exists to remove.
//
// Positional alignment with the row arrays below, and no column may normalise
// onto another's header key (brackets stripped, lowercased, non-alphanumerics
// dropped, LEFTMOST wins). These give taskid, activityname, taskdescription, date, asset,
// assetname, hoursworked, repairqty, repairrate, …, idlereason, owninghr.
// The deployment norm's context sits before Hours Worked, so the clerk reads
// the norm and the hours it implies and then types the hours actually run.
// These are the ABS sheet's machinery columns.
//
// Header names avoid every key the equipment parser reads (date/logdate,
// hoursworked/hours, asset/assetcode/machine, idlereason/remarks, taskid/task
// and each '<Head> Qty' / '<Head> Rate'): 'Target Hours' -> targethours is
// distinct from hours and hoursworked, since the match is on the whole key.
const NORM_COLUMNS = [
  "Non Prod Fac (ref)",
  "Utilization Factor (ref)",
  "Application Factor (ref)",
  "Production Norm (ref)",
  "Target Hours (ref)",
  "Pending Hours (ref)",
];
const FILLED_HEADERS = [
  "Task ID",
  "Activity Name (ref)",
  "Task Description (ref)",
  "Date",
  "Asset",
  "Asset Name (ref)",
  ...NORM_COLUMNS,
  "Hours Worked",
  ...HEAD_COLUMNS,
  "Idle Reason",
  "Owning ₹/hr (ref)",
];
const FILLED_WIDTHS = [
  22, 36, 34, 14, 12, 30,
  12, 15, 15, 15, 12, 14,
  13, ...VARIABLE_HEAD_KEYS.flatMap(() => [18, 14]), 28, 18,
];
// Four decimals on the factors and the norm, two on the hours.
const FILLED_NUM_FMTS: Record<number, string> = {
  7: "#,##0.000000",
  8: "#,##0.000000",
  9: "#,##0.000000",
  10: "#,##0.0000",
  11: "#,##0.00",
  12: "#,##0.00",
};
// The cells the clerk fills: hours and every head quantity/rate. Idle Reason is
// free text and stays plain, as Remarks does on the material sheet.
const INPUT_COLUMNS = [
  FILLED_HEADERS.indexOf("Hours Worked") + 1,
  ...HEAD_COLUMNS.map((header) => FILLED_HEADERS.indexOf(header) + 1),
];

const INSTRUCTIONS: string[][] = [
  ["Mechanical upload — Instructions"],
  [""],
  ["Task ID", "Required. The task number within this project, e.g. AE-0100/B3.1.1."],
  ["Date", "Required. Write it as 15-Sep-2026 (or 2026-09-15)."],
  ["Asset", "Required. The asset code (AST-0001) of a machine deployed on the task on that date."],
  ["Hours Worked", "Required. 0 is allowed for an idle machine — say why in Idle Reason."],
  ["Head Qty / Rate", "Optional pairs for Repair, Misc, Electricity, Fuel and Lubricants: hours in Qty (Hr), actual ₹ per hour in Rate. Amount = hours × rate. A head with both cells blank is not stored."],
  ["Owning cost", "Depreciation, Interest and Insurance are not in the file — they are the machine's rate card × Hours Worked, shown in the preview."],
  [""],
  ["Replace rule", "Each task-day in the file replaces what that task-day holds: machines missing from the file are removed (the preview names them). Meter readings, fuel litres and breakdown flags already stored are kept."],
  ["On a filled sheet", "You fill the amber columns: Hours Worked and the head quantities and rates. Task, date and machine are already filled in; the columns marked (ref) are for reading only."],
  ["Which tasks are listed", "The same tasks as the Mechanical screen: every task with deployed machines whose Civil execution is recorded on or before the date, within the screen's scope (tracked tasks, or all). A project with no tracking configured gets every such task."],
  ["Blank rows", "A machine row with nothing entered is skipped. On a task-day that has other rows, a machine left blank is removed — the preview names it."],
  ["Civil first", "A task needs Civil production on or before the date before machine hours can be logged against it. Pending Hours (ref) shows the hours still owed to that work."],
  ["Locked days", "A day whose daily progress report was submitted cannot be changed."],
  ["Reference columns", "Columns marked (ref) on a filled sheet are for reading only — the upload ignores them."],
];

const flattenTasks = (raw: any) =>
  normaliseDaySheet(raw).phases.flatMap((phase) =>
    phase.activities.flatMap((activity) => activity.tasks.map((task) => ({ ...task, activityName: activity.activityName })))
  );

const buildDaySheet =
  (projectId: number | string, scope: DaySheetScope) =>
  async (date: string): Promise<number> => {
  // The Mechanical screen's own population: tasks with deployed machines whose
  // Civil is done on or before the date, in the screen's scope. The server
  // widens the scope to every task when the project has no tracking at all.
  const res = await siteDailyApis.equipmentTasks(projectId, { date, scope, civilDone: true });
  if (res?.status !== true) throw new Error(res?.message || "Could not load the day sheet");
  const tasks = flattenTasks(res.data);

  // Civil first. Machine hours are booked against Civil work already done, so
  // a project with none has nothing to log against. Tell apart "nothing done
  // yet" from "done, but only on untracked tasks".
  if (!tasks.length) {
    const day = sheetDate(date);
    const doneAnywhere =
      scope === "ALL"
        ? 0
        : await siteDailyApis
            .equipmentTasks(projectId, { date, scope: "ALL", civilDone: true })
            .then((r: any) => (r?.status === true ? flattenTasks(r.data).length : 0))
            .catch(() => 0);
    if (doneAnywhere) {
      throw new DownloadBlocked(
        `Civil execution up to ${day} is recorded on ${doneAnywhere} task(s) with deployed machines, but none of them is tracked.`,
        "No tracked task has Civil done",
        "Switch the scope to All tasks on the Daily Execution page, or track those tasks in Task Monitoring.",
        "civil"
      );
    }
    throw new DownloadBlocked(
      `No task with deployed machines has Civil execution recorded on or before ${day}, so there is nothing to log machine hours against.`,
      "Civil execution required",
      "Record the Civil execution in the Civil tab (or upload it), then come back for this sheet.",
      "civil"
    );
  }
  const sheets = await inBatches(tasks, 4, (task) =>
    siteDailyApis
      .getEquipment(task.taskId, date)
      .then((r: any) =>
        r?.status === true ? { ...normaliseEquipmentSheet(r.data), activityName: task.activityName } : null
      )
      .catch(() => null)
  );
  const rows: any[][] = [];
  sheets.filter(Boolean).forEach((sheet: any) => {
    sheet.lines.forEach((line: any) => {
      const heads = new Map<string, any>(line.variableHeads.map((h: any) => [h.head, h]));
      const owningPerHour = line.fixedHeads.reduce(
        (sum: number, h: any) => (h.ratePerHour === "" ? sum : sum + Number(h.ratePerHour)),
        0
      );
      rows.push([
        sheet.taskSequenceId, sheet.activityName, sheet.taskName, sheetDate(date), line.assetCode, line.assetName,
        // Norm context, read-only. '' when the asset has no deployment norm on
        // the date — "no norm said", never a factor of zero.
        line.nonProdFactor === "" ? "" : Number(line.nonProdFactor),
        line.utilizationFactor === "" ? "" : Number(line.utilizationFactor),
        line.applicationFactor === "" ? "" : Number(line.applicationFactor),
        line.prodNormPerHour === "" ? "" : Number(line.prodNormPerHour),
        line.plannedHours === "" ? "" : Number(line.plannedHours),
        // Hours still owed to Civil work already done on the task, up to the date.
        line.pendingHours === "" ? "" : Number(line.pendingHours),
        line.hoursWorked === "" ? "" : Number(line.hoursWorked),
        ...VARIABLE_HEAD_KEYS.flatMap((key) => {
          const h = heads.get(key);
          return [h?.qty ? Number(h.qty) : "", h?.rate ? Number(h.rate) : ""];
        }),
        line.idleReason,
        line.rateStatus === "NO_RATE" ? "no rate card" : owningPerHour,
      ]);
    });
  });
  await writeValidatedWorkbook(
    `mechanical-${date}.xlsx`,
    {
      name: "Machines",
      headers: FILLED_HEADERS,
      widths: FILLED_WIDTHS,
      rows,
      inputColumns: INPUT_COLUMNS,
      numFmts: FILLED_NUM_FMTS,
    },
    INSTRUCTIONS
  );
  return rows.length;
};

const previewColumns: any[] = [
  statusColumn,
  rowColumn,
  {
    title: "Task",
    key: "task",
    width: 240,
    ellipsis: true,
    render: (_: any, row: any) => (
      <span>
        <b>{row.TaskID}</b> {row.taskName}
      </span>
    ),
  },
  { title: "Date", dataIndex: "Date", key: "Date", width: 104 },
  {
    title: "Machine",
    key: "machine",
    width: 220,
    ellipsis: true,
    render: (_: any, row: any) => (
      <span>
        <b>{row.Asset}</b> {row.assetName}
      </span>
    ),
  },
  numberColumn("Hours", "HoursWorked", 80),
  {
    title: "Operational heads",
    key: "heads",
    width: 280,
    render: (_: any, row: any) => {
      const parts = VARIABLE_HEAD_KEYS.map((key) => {
        const qty = row[`${HEAD_LABEL[key]}Qty`];
        const rate = row[`${HEAD_LABEL[key]}Rate`];
        if (!qty && !rate) return null;
        return `${HEAD_LABEL[key]} ${qty || "—"} ${HEAD_UNIT[key]} × ₹${rate || "—"}`;
      }).filter(Boolean);
      return parts.length ? (
        <span style={{ fontSize: 12 }}>{parts.join(" · ")}</span>
      ) : (
        <span style={{ color: "#8094ae" }}>—</span>
      );
    },
  },
  moneyColumn("Owning ₹", "OwningCost", 120),
  moneyColumn("Operational ₹", "OperationalCost", 120),
  moneyColumn("Total ₹", "TotalCost", 120),
  {
    title: "₹ / unit",
    key: "perUnit",
    width: 130,
    align: "right" as const,
    render: (_: any, row: any) =>
      row.PerUnit === "" || row.PerUnit == null ? (
        <span style={{ color: "#8094ae" }}>—</span>
      ) : (
        <span className="tabular-nums">
          ₹{Number(row.PerUnit).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          <span style={{ color: "#8094ae" }}> / {/cubi[ct]\s*met/i.test(row.workUomName || "") ? "Cum" : row.workUomName || "unit"}</span>
        </span>
      ),
  },
  { title: "Idle reason", dataIndex: "IdleReason", key: "IdleReason", width: 180, ellipsis: true },
  ...messageColumns,
];

const taskDays = (report: UploadReportState) =>
  new Set(report.rows.map((row: any) => `${row.TaskID}|${row.Date}`)).size;

const EquipmentLogUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
  initialDate,
  scope = "MONITORED",
  onRecordCivil,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
  initialDate?: string;
  // The Daily Execution page's scope, so the sheet lists what the screen lists.
  scope?: DaySheetScope;
  // Where "Record Civil execution" goes when the day has no output to log
  // against. The page owns the routing; this dialog only offers the button.
  onRecordCivil?: (date: string) => void;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Mechanical — Excel upload",
      entityLabel: "Machine logs",
      introSection: (
        <FilledSheetDownload
          label="Or start from a day's sheet"
          initialDate={initialDate}
          hint="The same tasks the Mechanical screen lists — every task with Civil done up to that date — with its deployed machines already filled in. Fill the amber columns — hours and the cost heads — and upload it back."
          build={buildDaySheet(projectId, scope)}
          actions={
            onRecordCivil
              ? { civil: { label: "Record Civil execution", run: onRecordCivil } }
              : undefined
          }
        />
      ),
      startUpload: (formData) => siteDailyApis.uploadEquipmentExcel(formData),
      buildBody: (comments) => ({ projectId: String(projectId), comment: comments ? comments : "N/A" }),
      previewColumns,
      previewScrollX: 2100,
      previewPageSize: 100,
      bucketRows,
      summaryTags: (report: UploadReportState) =>
        report.rows.length ? <Tag color="processing">{taskDays(report)} task-day(s)</Tag> : null,
      fullScreen: true,
      confirmClose: true,
      reportExport: {
        fileName: "mechanical-upload-report.xlsx",
        columns: [
          { header: "Status", width: 12, value: reportStatus },
          { header: "Row", width: 8, value: (row: any) => row.Row ?? "" },
          { header: "Task ID", width: 22, value: (row: any) => row.TaskID || "" },
          { header: "Date", width: 12, value: (row: any) => row.Date || "" },
          { header: "Asset", width: 12, value: (row: any) => row.Asset || "" },
          { header: "Machine", width: 28, value: (row: any) => row.assetName || "" },
          { header: "Hours", width: 8, value: (row: any) => row.HoursWorked ?? "" },
          { header: "Owning ₹", width: 14, value: (row: any) => row.OwningCost ?? "" },
          { header: "Operational ₹", width: 14, value: (row: any) => row.OperationalCost ?? "" },
          { header: "Total ₹", width: 14, value: (row: any) => row.TotalCost ?? "" },
          { header: "₹ / unit", width: 12, value: (row: any) => row.PerUnit ?? "" },
          ...reportMessageColumns,
        ],
      },
      rowErrorLabel: (row: any) => `Row ${row.Row ?? ""}`,
      copy: {
        uploadIntro: (
          <p>
            One row per machine per task-day: <b>Task ID | Date | Asset | Hours Worked</b>, then a quantity and rate for
            each operational cost head. Owning cost comes from the rate card and is shown in the preview. Each task-day
            in the file <b>replaces what that task-day holds</b> — machines you leave out are removed.
          </p>
        ),
        validatingHeading: "Checking your file…",
        validatingSub: "Matching tasks and machines, pricing owning and operational cost, and running the same checks the sheet's Save runs.",
        committingHeading: "Saving machine logs…",
        committingSub: "Saving every task-day in one go — if anything fails, nothing is saved.",
        commitCta: "Save machine logs",
        doneHeading: "Machine logs saved",
        doneSub: (report: UploadReportState) => <>{taskDays(report)} task-day(s) saved from {report.rows.length} row(s).</>,
        creationFailedNote: "Nothing was saved — see the reason below.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId, initialDate, scope, onRecordCivil]
  );

  return (
    <ExcelUploadStageModal config={config} open={open} onClose={onClose} onSuccess={onSuccess} projectId={projectId} />
  );
};

export default EquipmentLogUploadModal;
